import React from 'react';
import { Loader2Icon, CheckCircle2Icon, XCircleIcon, FileTextIcon } from 'lucide-react';
import { showInConciseMode } from '../chat/threadSettings';
import { cn } from '@/lib/utils';
import { Card } from '../ui/card';

interface RequestHelpToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

// Friendly labels for the request category (avoid raw enum values in the UI).
const CATEGORY_LABELS: Record<string, string> = {
  new_data_source: 'New data source',
  data_enrichment: 'Data enrichment',
  modeling: 'Data modeling',
  other: 'General',
};

const RequestHelpToolCallComponent: React.FC<RequestHelpToolCallProps> = ({
  args,
  argsText,
  result,
  status,
}) => {
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = !isRunning && !isError && result !== undefined;

  // Title/category come from the request args.
  let title = '';
  let category = '';
  try {
    const parsed = typeof args === 'object' && args ? args : JSON.parse(argsText);
    title = parsed?.title || '';
    category = parsed?.category || '';
  } catch {
    title = '';
  }

  let isSuccess = false;
  let errorMessage = '';
  if (isComplete && result) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      isSuccess = parsed?.success === true;
      if (!isSuccess) {
        errorMessage = parsed?.error || 'Could not send your request';
      }
    } catch {
      isSuccess = false;
    }
  }

  const hasError = isError || (isComplete && !isSuccess);
  const categoryLabel = category ? CATEGORY_LABELS[category] || category : '';

  return (
    <Card>
      <div
        className={cn(
          'p-3',
          isRunning ? 'bg-primary/10 border-b border-primary/20' : 'bg-muted/50 border-b border-border'
        )}
      >
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1">
            {isRunning ? (
              <div className="flex items-center gap-2">
                <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
                <p className="text-sm m-0 text-foreground">Sending your request to the data team…</p>
              </div>
            ) : hasError ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <FileTextIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">Help request</p>
                </div>
                <div className="flex items-center gap-2">
                  <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
                  <p className="text-sm m-0 text-destructive">
                    {errorMessage || 'Could not send your request — please try again.'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                  <p className="text-sm m-0 font-medium text-foreground">Sent to the data team</p>
                </div>
                {title && <p className="text-sm m-0 text-foreground">{title}</p>}
                <p className="text-xs m-0 text-muted-foreground">
                  {categoryLabel ? `${categoryLabel} · ` : ''}Pending review — the data team will follow up.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export const RequestHelpToolCall = RequestHelpToolCallComponent as React.ComponentType<any>;

// A user-initiated request — keep visible even in concise mode.
showInConciseMode(RequestHelpToolCall);
